import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/apiClient';
import { Button } from './ui/button';
import { 
  Home,
  Target,
  Megaphone,
  Lightbulb,
  Video,
  GitBranch,
  Film,
  LogOut,
  Settings,
  ChevronDown,
  ChevronLeft,
  Menu,
  X,
  Building2,
  CheckCircle2,
  MoreHorizontal
} from 'lucide-react';
import { toast } from 'sonner';

const AppLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  
  const [activeBusiness, setActiveBusiness] = useState(null);
  const [allBusinesses, setAllBusinesses] = useState([]);
  const [showBusinessDropdown, setShowBusinessDropdown] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  const menuItems = [
    { id: 'home', label: 'Home', icon: Home, path: '/dashboard', color: 'text-blue-600' },
    { id: 'icp', label: 'Arquitecto ICP', icon: Target, path: '/icp', color: 'text-cyan-600' },
    { id: 'campaigns', label: 'Campañas', icon: Megaphone, path: '/campaign-generator', color: 'text-purple-600' },
    { id: 'content', label: 'Contenido', icon: Lightbulb, path: '/content-generator', color: 'text-orange-600' },
    { id: 'scripts', label: 'Scripts', icon: Video, path: '/script-generator', color: 'text-red-600' },
    { id: 'funnels', label: 'Embudos', icon: GitBranch, path: '/funnel-builder', color: 'text-green-600' },
    { id: 'monica', label: 'MÓNICA', icon: Film, path: '/monica', color: 'text-pink-600' },
  ];

  // Items shown in mobile bottom nav (first 4 + more)
  const mobileNavItems = menuItems.slice(0, 4);
  const moreMenuItems = menuItems.slice(4);

  const loadBusinessData = async () => {
    try {
      const [activeResponse, allResponse] = await Promise.all([
        apiClient.get('/user/active-business'),
        apiClient.get('/businesses')
      ]);
      setActiveBusiness(activeResponse.data.business);
      setAllBusinesses(allResponse.data);
    } catch (error) {
      console.error('Error loading business data:', error);
    }
  };

  useEffect(() => {
    loadBusinessData();
  }, []);

  const handleChangeActiveBusiness = async (businessId) => {
    try {
      const response = await apiClient.put(`/businesses/${businessId}/set-active`);
      setActiveBusiness(response.data.business);
      setShowBusinessDropdown(false);
      toast.success('Empresa activa actualizada');
      window.location.reload();
    } catch (error) {
      console.error('Error changing active business:', error);
      toast.error('Error al cambiar empresa activa');
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex flex-col bg-white border-r border-slate-200 transition-all duration-300 ${
        sidebarCollapsed ? 'w-20' : 'w-64'
      }`}>
        {/* Logo/Brand */}
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center justify-between">
            {!sidebarCollapsed && (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">M</span>
                </div>
                <span className="font-bold text-slate-900">Marketing AI</span>
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-2"
            >
              <ChevronLeft className={`w-4 h-4 transition-transform ${sidebarCollapsed ? 'rotate-180' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Business Selector (Minimalist) */}
        {activeBusiness && (
          <div className="p-3 border-b border-slate-200">
            <div className="relative">
              <button
                onClick={() => setShowBusinessDropdown(!showBusinessDropdown)}
                className={`w-full flex items-center gap-2 p-2 rounded-lg hover:bg-slate-100 transition-colors ${
                  sidebarCollapsed ? 'justify-center' : ''
                }`}
              >
                <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-4 h-4 text-white" />
                </div>
                {!sidebarCollapsed && (
                  <>
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-xs font-medium text-slate-900 truncate">
                        {activeBusiness.business_name || activeBusiness.business_description?.substring(0, 20) + '...'}
                      </p>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showBusinessDropdown ? 'rotate-180' : ''}`} />
                  </>
                )}
              </button>

              {/* Business Dropdown */}
              {showBusinessDropdown && (
                <div className={`absolute top-full left-0 right-0 mt-1 bg-white rounded-lg border border-slate-200 shadow-lg z-50 max-h-64 overflow-y-auto ${
                  sidebarCollapsed ? 'left-full ml-2 w-48' : ''
                }`}>
                  {allBusinesses.map((business) => (
                    <button
                      key={business.id}
                      onClick={() => handleChangeActiveBusiness(business.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 transition-colors text-left ${
                        business.id === activeBusiness.id ? 'bg-purple-50' : ''
                      }`}
                    >
                      <span className="text-xs text-slate-700 flex-1 truncate">
                        {business.business_name || business.business_description?.substring(0, 25) + '...'}
                      </span>
                      {business.id === activeBusiness.id && (
                        <CheckCircle2 className="w-4 h-4 text-purple-600 flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Navigation Menu */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.id}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  active 
                    ? 'bg-blue-50 text-blue-700' 
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                } ${sidebarCollapsed ? 'justify-center' : ''}`}
                title={sidebarCollapsed ? item.label : ''}
              >
                <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-blue-600' : item.color}`} />
                {!sidebarCollapsed && (
                  <span className="text-sm font-medium">{item.label}</span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Bottom Section */}
        <div className="p-3 border-t border-slate-200 space-y-1">
          <button
            onClick={() => navigate('/businesses')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-all ${
              sidebarCollapsed ? 'justify-center' : ''
            }`}
            title={sidebarCollapsed ? 'Configuración' : ''}
          >
            <Settings className="w-5 h-5 flex-shrink-0" />
            {!sidebarCollapsed && <span className="text-sm font-medium">Empresas</span>}
          </button>
          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 hover:bg-red-50 hover:text-red-600 transition-all ${
              sidebarCollapsed ? 'justify-center' : ''
            }`}
            title={sidebarCollapsed ? 'Cerrar Sesión' : ''}
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {!sidebarCollapsed && <span className="text-sm font-medium">Cerrar Sesión</span>}
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Mobile Header */}
        <header className="lg:hidden bg-white border-b border-slate-200 px-4 py-3 sticky top-0 z-40">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">M</span>
              </div>
              {activeBusiness && (
                <div className="relative">
                  <button
                    onClick={() => setShowBusinessDropdown(!showBusinessDropdown)}
                    className="flex items-center gap-1 text-sm font-medium text-slate-700"
                  >
                    <span className="max-w-[120px] truncate">
                      {activeBusiness.business_name || activeBusiness.business_description?.substring(0, 15) + '...'}
                    </span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  
                  {showBusinessDropdown && (
                    <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg border border-slate-200 shadow-lg z-50 max-h-64 overflow-y-auto">
                      {allBusinesses.map((business) => (
                        <button
                          key={business.id}
                          onClick={() => handleChangeActiveBusiness(business.id)}
                          className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 transition-colors text-left ${
                            business.id === activeBusiness.id ? 'bg-purple-50' : ''
                          }`}
                        >
                          <span className="text-sm text-slate-700 flex-1 truncate">
                            {business.business_name || business.business_description?.substring(0, 30) + '...'}
                          </span>
                          {business.id === activeBusiness.id && (
                            <CheckCircle2 className="w-4 h-4 text-purple-600" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto pb-20 lg:pb-0">
          {children}
        </main>

        {/* Mobile Bottom Navigation */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-2 py-2 z-40">
          <div className="flex items-center justify-around">
            {mobileNavItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <button
                  key={item.id}
                  onClick={() => navigate(item.path)}
                  className={`flex flex-col items-center gap-1 px-3 py-1.5 rounded-lg transition-all ${
                    active ? 'text-blue-600' : 'text-slate-500'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${active ? 'text-blue-600' : ''}`} />
                  <span className="text-[10px] font-medium">{item.label}</span>
                </button>
              );
            })}
            
            {/* More Menu */}
            <div className="relative">
              <button
                onClick={() => setShowMoreMenu(!showMoreMenu)}
                className={`flex flex-col items-center gap-1 px-3 py-1.5 rounded-lg transition-all ${
                  showMoreMenu ? 'text-blue-600' : 'text-slate-500'
                }`}
              >
                <MoreHorizontal className="w-5 h-5" />
                <span className="text-[10px] font-medium">Más</span>
              </button>
              
              {showMoreMenu && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setShowMoreMenu(false)}
                  />
                  <div className="absolute bottom-full right-0 mb-2 w-48 bg-white rounded-lg border border-slate-200 shadow-lg z-50 overflow-hidden">
                    {moreMenuItems.map((item) => {
                      const Icon = item.icon;
                      const active = isActive(item.path);
                      return (
                        <button
                          key={item.id}
                          onClick={() => {
                            navigate(item.path);
                            setShowMoreMenu(false);
                          }}
                          className={`w-full flex items-center gap-3 px-4 py-3 transition-all ${
                            active 
                              ? 'bg-blue-50 text-blue-700' 
                              : 'text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          <Icon className={`w-5 h-5 ${active ? 'text-blue-600' : item.color}`} />
                          <span className="text-sm font-medium">{item.label}</span>
                        </button>
                      );
                    })}
                    <div className="border-t border-slate-200">
                      <button
                        onClick={() => {
                          navigate('/businesses');
                          setShowMoreMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50"
                      >
                        <Settings className="w-5 h-5" />
                        <span className="text-sm font-medium">Empresas</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </nav>
      </div>
      
      {/* Click outside to close dropdowns */}
      {showBusinessDropdown && (
        <div 
          className="fixed inset-0 z-30" 
          onClick={() => setShowBusinessDropdown(false)}
        />
      )}
    </div>
  );
};

export default AppLayout;
